import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLabel, IPScrollablePane, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPDefaultButton, IPIconButton, IPPrimaryButton } from 'baseComponents/Button/IPButton';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import Accordion from 'components/Accordion/Accordion';
import { AuditScopeItemLink } from 'components/Audit/AuditScopeItemLink';
import EvidenceTaskLinks from 'components/AuditCriteria/EvidenceTaskLinks';
import { CopilotActionButton } from 'components/Copilot/CopilotButtons';
import { CopilotMarkdown } from 'components/Copilot/Panel/Conversation/CopilotMarkdown';
import HrefLink from 'components/Links/HrefLink';
import { Info } from 'components/Notification/Info';
import BaseTableList from 'components/TableList/BaseTableList';
import RichTextEditor from 'components/Text/RichTextEditor';
import RichTextReadOnly from 'components/Text/RichTextReadOnly';
import { getEntityIcon, getEntityTypeName } from 'globalFunctions';
import {
  copilotIconProps,
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import { TFunction } from 'i18next';
import {
  auditCriterionModeOneTime,
  auditCriterionNotAuditedNoEvidence,
  AuditEntityKey,
  auditEntityKeyString,
  getAuditCheckStatusText,
  getAuditCriterionNotAuditedText,
} from 'models/auditCriteria';
import Entity, { EntityTypes } from 'models/entity';
import InternalAuditModel, { AuditOutcome, AuditStatus } from 'models/internalAudit';
import {
  AiControlAuditCheckRow,
  getAiAuditNotAssessedText,
  getAiAuditVerdictText,
  InternalAuditConclusionReason,
  InternalAuditControlAuditResult,
  mapAiReportStatusToOutcome,
} from 'models/internalAuditAiAudit';
import {
  auditConclusionMaxLength,
  AuditResultBlock,
  getAuditResultBlock,
  InternalAuditTaskExecution,
  InternalAuditTaskExecutionItem,
} from 'models/internalAuditExecution';
import ResourceLink from 'models/resourceLink';
import { AIEnabled } from 'models/setting';
import Task, { TaskStateId } from 'models/tasks/task';
import { Fragment, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiCreateInternalAuditTaskAiAuditReport,
  apiGetInternalAuditTaskAiAudit,
  apiGetInternalAuditTaskExecution,
  apiSetInternalAuditTaskResult,
  apiSuggestInternalAuditTaskConclusion,
} from 'services/Api/internalAuditService';
import { apiGetLinksForId } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasSubscriptionFeature, hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { toLocaleDateLong, toLocaleDateShort } from 'utils/datetime';
import { htmlToText, textToHtmlParagraphs } from 'utils/html';
import { getRandomId } from 'utils/string';

interface IAuditExecutionTaskDetail {
  task: Task;
  orgTask: Task;
  getErrorMessage: (field: string) => string | undefined;
  isActionPending: boolean;
  windowLevel?: number;
  navigateExternal?: boolean;
  canUpdate: boolean;
  readOnly?: boolean;
  onResultBlockChange?: (block: AuditResultBlock) => void;
  onResultDirtyChange?: (isDirty: boolean) => void;
  // the result is written by the task save: this hands the parent the flush to run before it
  onRegisterSaveResult?: (saveResult: (() => Promise<void>) | undefined) => void;
  // the audit this task belongs to: the parent hides Reopen once that audit is published
  onAuditStatusChange?: (status: AuditStatus | undefined) => void;
  // The audited items, keyed the way the projected form fields are and under the name the auditor
  // knows them by. This pane is the one that reads them, and the parent needs them to head the
  // questions of each entity on the form and to label the buttons that open their criteria.
  onEntityNamesChange?: (namesByKey: Map<string, string>) => void;
  // Suggest conclusion reads the STORED task, so the parent has to flush whatever it is holding first.
  // false means it could not (a form validation error, or the save failed) and nothing is asked.
  onFlushDraft?: () => Promise<boolean>;
  // the AI report the server just wrote and attached to this task and audited item: the parent puts it into the
  // task's link state, so the Library tab shows it and the next save does not drop it again
  onReportLinkCreated?: (link: ResourceLink) => void;
  // opens the evidence task a one-time check is bound to. Owned by the parent, which already holds
  // the panel that shows another task
  onOpenEvidenceTask?: (taskId: number) => void;
  // The take-over of an AI result also adopts its per-check answers, and those live on the FORM -
  // which this pane does not own: the parent hands it to its TaskTypeForm for the audited item named
  // here. Without it the take-over fills the outcome and the conclusion only.
  onTakeOverAiAnswers?: (entity: AuditEntityKey) => void;
  compact?: boolean;
  compactActions?: ReactNode;
  compactTaskContent?: ReactNode;
  leadingCommands?: ReactNode;
  trailingCommands?: ReactNode;
}

// The focused auditee view of an audit execution task: the audit and the scope item(s) to audit,
// the instruction, and the result (outcome + conclusion, written onto the audit's scope row). The
// outcome pre-fills from the previous audit; the previous conclusion sits behind a collapsed panel
// with a one-click "use previous text" - never silently copied into this year's evidence.
// The result has its own endpoint but no save button of its own: the panel's Save writes it through
// the flush registered with onRegisterSaveResult, so the task and its result are one action.
export const AuditExecutionTaskDetail = (props: IAuditExecutionTaskDetail) => {
  const { t } = useTranslation(['audit', 'translation', 'criteria']);
  const appContext = useContext(AppContext);

  const [execution, setExecution] = useState<InternalAuditTaskExecution | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [outcome, setOutcome] = useState<AuditOutcome>(AuditOutcome.NotAssessed);
  const [conclusion, setConclusion] = useState<string | undefined>(undefined);
  // the AI control audit's results for the items this task covers (empty when never run)
  const [aiResults, setAiResults] = useState<InternalAuditControlAuditResult[]>([]);
  // the editor was filled from the AI proposal: recorded with the save so the trail shows it
  const [adoptedFromAi, setAdoptedFromAi] = useState<boolean>(false);
  const [isSuggesting, setIsSuggesting] = useState<boolean>(false);
  // which control's report is being written to the library right now (one button pair per control)
  const [savingReportControlId, setSavingReportControlId] = useState<number | undefined>(undefined);
  const [savedOutcome, setSavedOutcome] = useState<AuditOutcome>(AuditOutcome.NotAssessed);
  const [savedConclusion, setSavedConclusion] = useState<string | undefined>(undefined);
  const [showPreviousResults, setShowPreviousResults] = useState<boolean>(false);
  const [showAiResults, setShowAiResults] = useState<boolean>(false);
  const [expandedPreviousItems, setExpandedPreviousItems] = useState<string[]>([]);
  const [expandedAiItems, setExpandedAiItems] = useState<number[]>([]);
  const [hasToggledResultAccordion, setHasToggledResultAccordion] = useState<boolean>(false);
  // the compact pane collapses the conclusion editor back to its read-only box when focus leaves it:
  // a new key is what puts the editor back, so the narrow panel is not owned by a toolbar
  const [conclusionUnFocusKey, setConclusionUnFocusKey] = useState<string>(getRandomId());
  const [itemColumns, setItemColumns] = useState<IColumn[]>([
    { key: 'type', name: '', minWidth: 90, maxWidth: 140, isResizable: true },
    { key: 'item', name: '', minWidth: 160, isMultiline: true, isResizable: true },
  ]);

  // two records, two reasons to freeze: a completed audit locks its conclusions until it is moved
  // back (a published one for good), a finished task holds the answer it was closed with - reopening
  // the task is what makes that one editable again
  const auditFrozen = (execution?.auditStatus ?? 0) >= AuditStatus.Completed;
  const auditPublished = (execution?.auditStatus ?? 0) >= AuditStatus.Published;
  const taskFrozen =
    props.orgTask.taskStateId === TaskStateId.Completed || props.orgTask.taskStateId === TaskStateId.Archived;
  const resultsFrozen = props.readOnly || auditFrozen || taskFrozen;
  const isGenericManager = hasUserFeatureGenericManager(appContext);
  const aiEnabled = (appContext.globalDataCache.settings.get(AIEnabled) as boolean) === true;
  // Suggest conclusion is offered on the SUBSCRIPTION axis only: the auditee doing the work is very
  // often a plain User, and hasUserFeature(AIAssistant) checks the app role too, which would deny
  // exactly the people this button is for. Same shape as the server's HasAuditParticipantAccess.
  const canSuggestConclusion =
    !resultsFrozen &&
    Boolean(props.onFlushDraft) &&
    aiEnabled &&
    hasSubscriptionFeature(appContext.user.login.subscriptionType, FeatureTypes.AIAssistant);
  const resultAccordionCount = aiResults.length + (execution?.items.filter((item) => item.previous).length ?? 0);

  useEffect(() => {
    setExpandedPreviousItems([]);
    setExpandedAiItems([]);
    setHasToggledResultAccordion(false);
  }, [props.task.taskId]);

  const getItemKey = (item: InternalAuditTaskExecutionItem): string => `${item.entityType}-${item.entityId}`;

  const getTaskTitleItem = (): InternalAuditTaskExecutionItem | undefined => {
    const items = execution?.items ?? [];

    return items.length === 1 ? items[0] : undefined;
  };

  const renderAuditName = () => {
    if (!execution) return null;
    if (!isGenericManager) return execution.auditName;

    return (
      <HrefLink href={`/audit/${execution.internalAuditId}/execute`} newTab>
        {execution.auditName}
      </HrefLink>
    );
  };

  // Undefined when there is nothing to read, which hides the (i) itself: an icon that opens to say
  // "no instruction" is a promise of help that is not there. The instruction is stored HTML, so it
  // goes into the tooltip as JSX - a string would show its tags - and transparent and borderless,
  // because the read-only control's own frame and background read as a box drawn inside the bubble
  const renderInstruction = (): JSX.Element | undefined => {
    if (!htmlToText(props.task.description).trim()) return undefined;

    return <RichTextReadOnly html={props.task.description} maxHeight={300} transparent hideBorder />;
  };

  const seedEditor = (result: InternalAuditTaskExecution | undefined) => {
    const item = result?.items[0];
    if (!item) return;

    const isAssessed = item.assessedOn !== undefined || item.outcome !== AuditOutcome.NotAssessed;
    // the outcome pre-fills from the previous audit; the conclusion never does
    const seededOutcome = isAssessed ? item.outcome : item.previous?.outcome ?? AuditOutcome.NotAssessed;
    // one result covers every item of this task, so a text is only pre-filled when the items agree
    // on it: showing the first item's conclusion and saving it over a different one is silent loss
    const items = result?.items ?? [];
    const sameConclusion = items.every((i) => (i.conclusion ?? '') === (item.conclusion ?? ''));

    setOutcome(seededOutcome);
    setConclusion(sameConclusion ? item.conclusion : undefined);
    setSavedOutcome(item.outcome);
    setSavedConclusion(sameConclusion ? item.conclusion : undefined);
  };

  const loadData = async () => {
    if (props.task.taskId <= 0) return;

    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiGetInternalAuditTaskExecution(props.task.taskId, accessToken);
      setExecution(result);
      seedEditor(result);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.taskId]);

  // Best effort: the AI results are an enrichment of the pane, never a blocker for it.
  useEffect(() => {
    const loadAiResults = async () => {
      if (props.task.taskId <= 0) return;
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const results = await apiGetInternalAuditTaskAiAudit(props.task.taskId, accessToken);
        setAiResults(results);
      } catch {
        setAiResults([]);
      }
    };

    loadAiResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.taskId]);

  // the parent gates Finish on the result and enables its Save on a result-only edit: both are
  // judged on the editor, because the panel's Save is what stores what is typed there. Finishing
  // without an outcome or a conclusion would leave the audit's scope row without evidence
  useEffect(() => {
    props.onResultBlockChange?.(
      !execution || resultsFrozen ? AuditResultBlock.None : getAuditResultBlock(outcome, conclusion),
    );
    props.onResultDirtyChange?.(execution !== undefined && !resultsFrozen && resultChanged());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execution, resultsFrozen, savedOutcome, savedConclusion, outcome, conclusion]);

  // a published audit is the compliance record of what happened: the parent uses this to hide Reopen
  useEffect(() => {
    props.onAuditStatusChange?.(execution ? (execution.auditStatus as AuditStatus) : undefined);

    // the names the auditor knows the audited items by, keyed the way the projected form fields
    // are (requirements share the control table, so their key folds to the control family too)
    const namesByKey = new Map<string, string>();
    (execution?.items ?? []).forEach((item) =>
      namesByKey.set(
        auditEntityKeyString(item.entityType, item.entityId),
        [item.code, item.name].filter(Boolean).join(' '),
      ),
    );
    props.onEntityNamesChange?.(namesByKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execution]);

  // The result write, run by the parent's pre-save hook when the panel is saved. Errors are
  // deliberately not caught: SingleTask reports them and abandons the task save, so a refused
  // result (a frozen audit) never ends up with the task saved as if it had been recorded.
  const saveResult = async (): Promise<void> => {
    if (!execution || resultsFrozen || !resultChanged()) return;

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const result = await apiSetInternalAuditTaskResult(
      props.task.taskId,
      outcome,
      conclusion,
      accessToken,
      adoptedFromAi,
    );
    setExecution(
      result
        ? {
            ...result,
            items: result.items.map((item) => ({ ...item, outcome, conclusion })),
          }
        : result,
    );
    setSavedOutcome(outcome);
    setSavedConclusion(conclusion);
    // the adoption is recorded with THIS save; a later manual edit is the human's own work
    setAdoptedFromAi(false);
  };

  // re-registered on every change so the parent always holds a flush over the current editor state
  useEffect(() => {
    props.onRegisterSaveResult?.(saveResult);

    return () => {
      props.onRegisterSaveResult?.(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execution, resultsFrozen, savedOutcome, savedConclusion, outcome, conclusion, adoptedFromAi]);

  const resultChanged = (): boolean => {
    return outcome !== savedOutcome || (conclusion ?? '') !== (savedConclusion ?? '');
  };

  // Each freeze has its own way out, and saying which one applies is the difference between a note
  // and a dead end: a finished task is reopened here, a completed audit is moved back on its
  // timeline, a published audit is the compliance record of what happened and stays as it is.
  const getFrozenText = (): string => {
    if (auditPublished) return t('audit:Execute.Task.FrozenPublished');
    if (auditFrozen) return t('audit:Execute.Task.Frozen');
    if (taskFrozen) return t('audit:Execute.Task.FrozenTask');

    return t('audit:Execute.Task.ReadOnly');
  };

  const getOutcomeOptions = (): IPSelectableOption[] => {
    return [
      AuditOutcome.NotAssessed,
      AuditOutcome.Conform,
      AuditOutcome.Observation,
      AuditOutcome.NonConformityMinor,
      AuditOutcome.NonConformityMajor,
    ].map((o) => ({ key: o, text: InternalAuditModel.getOutcomeText(o, t as TFunction<string[]>) }));
  };

  // A task that covers several items is a list, not a stack of labelled rows: one line per item in a
  // compact table reads at a glance and stays readable when a merge grows it further.
  const renderItemList = (items: InternalAuditTaskExecutionItem[]) => {
    return (
      <IPStack.Item>
        <IPLabel>{t('audit:Execute.Task.ItemLabel')}</IPLabel>
        <IPStack.Item styles={{ root: { height: Math.min(items.length * 36 + 12, 260) } }}>
          <BaseTableList
            compact
            hideHeader
            disableSort
            disableEmptyState
            noTopPadding
            items={items}
            columns={itemColumns}
            setColumns={setItemColumns}
            selectionMode={IPSelectionMode.none}
            getKey={(item: InternalAuditTaskExecutionItem) => `${item.entityType}-${item.entityId}`}
            onRenderCol={renderItemColumn}
          />
        </IPStack.Item>
      </IPStack.Item>
    );
  };

  const renderItemColumn = (item: InternalAuditTaskExecutionItem, column: IColumn): JSX.Element | null => {
    switch (column.key) {
      case 'type': {
        const entity = new Entity(item.entityId, item.entityType, item.name ?? '', item.code);

        return (
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item>{getEntityIcon(entity)}</IPStack.Item>
            <IPStack.Item>
              <IPText>{getEntityTypeName(item.entityType as EntityTypes, t as TFunction<string[]>)}</IPText>
            </IPStack.Item>
          </IPStack>
        );
      }

      case 'item':
        return (
          <AuditScopeItemLink
            entityType={item.entityType}
            entityId={item.entityId}
            code={item.code}
            name={item.name}
            auditTaskId={props.task.taskId}
            internalAuditId={execution?.internalAuditId ?? props.task.internalAuditId}
          />
        );

      default:
        return null;
    }
  };

  // One accordion per scope item ON PURPOSE: each item's previous result is looked up on its own
  // and may differ - it can even come from a different previous audit. With several items the item
  // is named in the title, so the two accordions can be told apart; the audit each result came
  // from is inside, above the outcome.
  const renderPreviousContent = (item: InternalAuditTaskExecutionItem) => {
    if (!item.previous) return null;

    const applyPreviousResult = () => {
      setOutcome(item.previous?.outcome ?? AuditOutcome.NotAssessed);
      setConclusion(item.previous?.conclusion);
      setExpandedPreviousItems((current) => current.filter((key) => key !== getItemKey(item)));
      setShowPreviousResults(false);
    };

    return (
      <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
        <IPStack.Item>
          <IPText variant="mediumPlus" styles={globalTextStylesBold}>
            {t('audit:Execute.Task.Previous.Audit', {
              audit: item.previous.auditName ?? '',
              date: item.previous.auditStartDate ? toLocaleDateShort(item.previous.auditStartDate) : '',
            })}
          </IPText>
        </IPStack.Item>
        <IPStack.Item>
          <IPText>
            {t('audit:Execute.Task.Previous.Outcome', {
              outcome: InternalAuditModel.getOutcomeText(item.previous.outcome, t as TFunction<string[]>),
              name: item.previous.assessedById
                ? appContext.globalDataCache.users.get(item.previous.assessedById)?.name ?? ''
                : '',
            })}
          </IPText>
        </IPStack.Item>
        {item.previous.conclusion && (
          <IPStack.Item>
            <RichTextReadOnly html={item.previous.conclusion} hideBorder />
          </IPStack.Item>
        )}
        {!resultsFrozen && (
          <IPStack.Item>
            <IPPrimaryButton text={t('audit:Execute.Task.Previous.UseText')} onClick={applyPreviousResult} />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  const renderPrevious = (item: InternalAuditTaskExecutionItem) => {
    if (!item.previous) return null;

    const multipleItems = (execution?.items.length ?? 0) > 1;
    const itemKey = getItemKey(item);

    return (
      <IPStack.Item key={`prev-${itemKey}`}>
        <Accordion
          title={
            multipleItems
              ? t('audit:Execute.Task.Previous.TitleForItem', { item: `${item.code ?? ''} ${item.name ?? ''}`.trim() })
              : t('audit:Execute.Task.Previous.Title')
          }
          showBorder
          maxHeight="none"
          active={expandedPreviousItems.includes(itemKey) || (!hasToggledResultAccordion && resultAccordionCount === 1)}
          onToggle={(expanded) => {
            setHasToggledResultAccordion(true);
            setExpandedPreviousItems((current) =>
              expanded
                ? [...current.filter((key) => key !== itemKey), itemKey]
                : current.filter((key) => key !== itemKey),
            );
          }}
        >
          {renderPreviousContent(item)}
        </Accordion>
      </IPStack.Item>
    );
  };

  //
  // The AI control audit result of a covered item: read, then take over. Nothing is stored until
  // the human saves the panel - AssessedById stays a real person.
  //

  const takeOverAiResult = (result: InternalAuditControlAuditResult) => {
    // outcome: the worst ASSESSED verdict, never NonConformityMajor (that judgement stays human).
    // A run that assessed nothing proposes no outcome - its status is a reason, not a verdict
    const proposedOutcome = mapAiReportStatusToOutcome(result.getWorstAssessedStatus());
    if (proposedOutcome !== undefined) {
      setOutcome(proposedOutcome);
    }

    const conclusionText = result.getConclusionText();
    if (conclusionText) {
      // the conclusion editor holds HTML; the summaries are plain text paragraphs
      setConclusion(textToHtmlParagraphs(conclusionText));
    }

    // The per-criterion verdicts live on the FORM as projected fields, with the AI's answer stored
    // beside the human one - so the take-over hands them to the form as well: an auditor who adopts
    // the verdict adopts the answers it was reached on, instead of repeating that per question. The
    // item is named because a task auditing several holds the questions of each of them, and this is
    // the verdict on one. Nothing is stored by any of it: the panel's save is.
    const item = execution?.items.find((i) => i.entityId === result.controlId);
    props.onTakeOverAiAnswers?.({
      entityType: item?.entityType ?? (EntityTypes.Control as number),
      entityId: result.controlId,
    });

    setAdoptedFromAi(true);
    setExpandedAiItems((current) => current.filter((controlId) => controlId !== result.controlId));
    setShowAiResults(false);

    // the full report lands in the task and audited item's Resources tabs; best effort, the adoption stands without it
    void createAiReport(result.controlId, false);
  };

  const createAiReport = async (controlId: number, notify: boolean) => {
    if (resultsFrozen) return;

    try {
      // per control: the accordion renders one button pair per covered item, and the pane-wide
      // isLoading would blank the whole pane
      setSavingReportControlId(controlId);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const link = await apiCreateInternalAuditTaskAiAuditReport(props.task.taskId, controlId, accessToken);
      if (link) {
        // the server created the document AND attached it to this task. The open panel holds the
        // link list it was opened with, so without this the Library tab stays empty and - worse -
        // the next save posts a link list without the report, which deletes the attachment again
        const links = await apiGetLinksForId(accessToken, [link.linkId], appContext.globalDataCache);
        if (links.length > 0) {
          props.onReportLinkCreated?.(links[0]);
        }
      }

      if (notify && link) {
        // raised from inside the task modal, so it belongs in the bottom-center container
        appContext.showNotification(
          t('audit:Execute.Task.AiAudit.ReportCreated', { name: link.linkName }),
          false,
          false,
          true,
        );
      }
    } catch (err) {
      if (notify) {
        appContext.setError(err);
      }
    } finally {
      setSavingReportControlId(undefined);
    }
  };

  const getSuggestFailedText = (reason: InternalAuditConclusionReason | undefined): string => {
    if (reason === InternalAuditConclusionReason.NoInput) {
      return t('audit:Execute.Task.AiAudit.SuggestNoInput');
    }

    if (reason === InternalAuditConclusionReason.Failed) {
      return t('audit:Execute.Task.AiAudit.SuggestError');
    }

    return t('audit:Execute.Task.AiAudit.SuggestFailed');
  };

  //
  // Suggest conclusion: the AI writes the conclusion and proposes the outcome from what the auditor
  // recorded - the questionnaire they worked, the follow-up findings they raised and, when the item
  // has one, the AI evidence audit. Nothing is stored until the panel saves, exactly like the
  // take-over above, so AssessedById stays a real person.
  //
  const suggestConclusion = async () => {
    try {
      setIsSuggesting(true);

      // the pane autosaves on a debounce, so the comment just typed against a failing check may not
      // be on the server yet - and the server reads the stored task
      const flushed = await props.onFlushDraft?.();
      if (flushed === false) {
        appContext.showNotification(t('audit:Execute.Task.AiAudit.SaveFirst'), true, false, true);

        return;
      }

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const suggestion = await apiSuggestInternalAuditTaskConclusion(props.task.taskId, accessToken);

      if (!suggestion || !suggestion.conclusion) {
        // nothing to offer, and the reason decides what to say: nothing recorded yet is the auditor's
        // own next step, an error is worth retrying, and anything else gets the plain statement. The
        // editor is left exactly as it is in every case
        appContext.showNotification(getSuggestFailedText(suggestion?.reason), true, false, true);

        return;
      }

      setConclusion(textToHtmlParagraphs(suggestion.conclusion));
      // NotAssessed means the server refused to propose one (it contradicts a recorded
      // non-conformity): the auditor picks it, so their current selection is left alone
      if (suggestion.outcome !== AuditOutcome.NotAssessed) {
        setOutcome(suggestion.outcome);
      }

      setAdoptedFromAi(true);
      setShowAiResults(false);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsSuggesting(false);
    }
  };

  // Offered in both layouts: in the compact footer callout next to the AI audit results, and under the
  // conclusion editor of the modal, which lists those results inline and needs no callout of its own.
  // The stack item is part of it, so a hidden button leaves no empty row taking a gap in either layout.
  const renderSuggestConclusion = () => {
    if (!canSuggestConclusion) return null;

    return (
      <IPStack.Item>
        <CopilotActionButton
          text={t('audit:Execute.Task.AiAudit.SuggestConclusion')}
          disabled={props.isActionPending}
          showSpinner={isSuggesting}
          onClick={() => void suggestConclusion()}
        />
      </IPStack.Item>
    );
  };

  // Why a criterion row carries no status. An unbound one-time criterion is the one case the run can
  // name precisely - there was no evidence task to inspect - and it is also the most actionable, so it
  // is worth more than the generic "not inspected" every other cause shares.
  const getCheckNotAuditedText = (check: AiControlAuditCheckRow): string => {
    if (check.mode === auditCriterionModeOneTime && !check.bound) {
      return getAuditCriterionNotAuditedText(auditCriterionNotAuditedNoEvidence, t as TFunction<string[]>) ?? '';
    }

    return t('audit:Execute.Task.AiAudit.NotInspected');
  };

  const renderAiAuditContent = (result: InternalAuditControlAuditResult) => {
    const isSavingReport = savingReportControlId === result.controlId;
    // a run whose reports were all guard messages ("no readable documents", "no monitoring tasks")
    // assessed nothing: it reads as Not assessed here, exactly as it does in the execute grid. The
    // conclusion paragraph below carries the reason
    const assessed = result.hasAssessment();
    const verdictText = assessed
      ? getAiAuditVerdictText(result.getWorstAssessedStatus(), t as TFunction<string[]>)
      : getAiAuditNotAssessedText(t as TFunction<string[]>);
    const findings = [...(result.documentation?.findings ?? []), ...(result.evidence?.findings ?? [])];
    // both mode reports carry the limitations of the same documentation set, so merged they repeat
    const limitations = [
      ...new Set([...(result.documentation?.limitations ?? []), ...(result.evidence?.limitations ?? [])]),
    ];
    // the reasoning behind the verdict, per mode: the very markdown the saved report document holds,
    // so the auditor can read WHY before deciding whether to take the result over
    const reports = [
      {
        label: t('audit:Execute.Task.AiAudit.ReportDocumentation'),
        markdown: result.documentation?.markdownReport ?? '',
      },
      { label: t('audit:Execute.Task.AiAudit.ReportEvidence'), markdown: result.evidence?.markdownReport ?? '' },
    ].filter((report) => report.markdown);
    // the model's own account of why each mode got its status: the auditor accepts or challenges the
    // verdict against a stated reason instead of a bare status
    const rationales = [
      { mode: t('audit:Execute.Task.AiAudit.ReportDocumentation'), text: result.documentation?.statusRationale },
      { mode: t('audit:Execute.Task.AiAudit.ReportEvidence'), text: result.evidence?.statusRationale },
    ].filter((rationale) => !!rationale.text);
    // documentation audit only: which linked documents counted and which did not
    const documentAssessments = result.documentation?.documentAssessments ?? [];
    const relevanceText = (relevance: string): string => {
      switch (relevance) {
        case 'High':
          return t('audit:Execute.Task.AiAudit.Relevance.High');
        case 'Medium':
          return t('audit:Execute.Task.AiAudit.Relevance.Medium');
        case 'Low':
          return t('audit:Execute.Task.AiAudit.Relevance.Low');
        case 'None':
          return t('audit:Execute.Task.AiAudit.Relevance.None');
        default:
          return relevance;
      }
    };

    return (
      <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            {!resultsFrozen && (
              <IPPrimaryButton
                text={t('audit:Execute.Task.AiAudit.TakeOver')}
                disabled={props.isActionPending || isSavingReport}
                onClick={() => takeOverAiResult(result)}
              />
            )}
            <IPDefaultButton
              text={t('audit:Execute.Task.AiAudit.SaveReport')}
              disabled={resultsFrozen || props.isActionPending || isSavingReport}
              onClick={() => void createAiReport(result.controlId, true)}
            />
            {isSavingReport && <IPSpinner size={IPSpinnerSize.small} />}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              {/* the verdict heads the whole result: bold, so it is still the first thing read after
                  scrolling back up through the report */}
              <IPText styles={globalTextStylesBold}>
                {t('audit:Execute.Task.AiAudit.Verdict', {
                  verdict: verdictText,
                  date: result.generatedOn ? toLocaleDateShort(result.generatedOn) : '',
                })}
              </IPText>
            </IPStack.Item>
            {rationales.map((rationale) => (
              <IPStack.Item key={rationale.mode}>
                <IPText block>
                  {`${t('audit:Execute.Task.AiAudit.StatusRationale', { mode: rationale.mode })}: ${rationale.text}`}
                </IPText>
              </IPStack.Item>
            ))}
            {result.getConclusionText() && (
              <IPStack.Item>
                <IPText>{result.getConclusionText()}</IPText>
              </IPStack.Item>
            )}
            {result.getCheckRows().length > 0 && (
              <IPStack.Item>
                <IPLabel>{t('audit:Execute.Task.AiAudit.Criteria')}</IPLabel>
                <IPStack tokens={globalStackTokensGapSmall}>
                  {result.getCheckRows().map((check, index) => (
                    <IPStack.Item key={`${check.checkKey}-${index}`}>
                      {/* the mode is part of what the auditor is being asked: a one-time criterion is
                          proved by evidence that already exists, a recurring one by ongoing practice.
                          The status is said in plain language - the raw name is a wire contract.
                          A one-time criterion with nothing bound to it names that reason instead of the
                          generic "not inspected", so this pane and the criterion's own form line agree */}
                      <IPText>
                        {`${check.requirement} - ${
                          getAuditCheckStatusText(check.evidenceStatus, t as TFunction<string[]>) ??
                          getCheckNotAuditedText(check)
                        } (${
                          check.mode === auditCriterionModeOneTime
                            ? t('criteria:Mode.OneTime')
                            : t('criteria:Mode.Recurring')
                        })`}
                      </IPText>
                      {/* a bound one-time criterion keeps its evidence on another task: name it, so the
                          auditor can open what the AI actually looked at */}
                      {check.bound && check.taskId !== undefined && props.onOpenEvidenceTask && (
                        <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
                          <IPStack.Item disableShrink>
                            <IPText>{t('criteria:Tasks.Label')}</IPText>
                          </IPStack.Item>
                          <IPStack.Item grow shrink styles={{ root: { minWidth: 0 } }}>
                            <EvidenceTaskLinks
                              tasks={[{ taskId: check.taskId }]}
                              hideEmpty
                              onOpenTask={props.onOpenEvidenceTask}
                            />
                          </IPStack.Item>
                        </IPStack>
                      )}
                    </IPStack.Item>
                  ))}
                </IPStack>
              </IPStack.Item>
            )}
            {documentAssessments.length > 0 && (
              <IPStack.Item>
                <IPLabel>{t('audit:Execute.Task.AiAudit.DocumentAssessments')}</IPLabel>
                <IPStack tokens={globalStackTokensGapSmall}>
                  {documentAssessments.map((assessment, index) => (
                    <IPStack.Item key={`assessment-${index}`}>
                      <IPText block styles={globalTextStylesBold}>
                        {`${assessment.name ?? `#${assessment.linkId}`} - ${relevanceText(assessment.relevance)}`}
                      </IPText>
                      {assessment.rationale && <IPText block>{assessment.rationale}</IPText>}
                    </IPStack.Item>
                  ))}
                </IPStack>
              </IPStack.Item>
            )}
            {findings.length > 0 && (
              <IPStack.Item>
                <IPLabel>{t('audit:Execute.Task.AiAudit.Findings')}</IPLabel>
                <IPStack tokens={globalStackTokensGapSmall}>
                  {findings.map((finding, index) => (
                    <IPStack.Item key={`finding-${index}`}>
                      {/* the observation is what the AI saw; the risk and the recommendation are why it
                          matters and what it proposes - an auditor judging the verdict needs all three */}
                      <IPText block styles={globalTextStylesBold}>
                        {finding.title}
                      </IPText>
                      {finding.observation && <IPText block>{finding.observation}</IPText>}
                      {finding.risk && (
                        <IPText block>{`${t('audit:Execute.Task.AiAudit.FindingRisk')}: ${finding.risk}`}</IPText>
                      )}
                      {finding.recommendation && (
                        <IPText block>
                          {`${t('audit:Execute.Task.AiAudit.FindingRecommendation')}: ${finding.recommendation}`}
                        </IPText>
                      )}
                    </IPStack.Item>
                  ))}
                </IPStack>
              </IPStack.Item>
            )}
            {limitations.length > 0 && (
              <IPStack.Item>
                <IPLabel>{t('audit:Execute.Task.AiAudit.Limitations')}</IPLabel>
                <IPStack tokens={globalStackTokensGapSmall}>
                  {limitations.map((limitation, index) => (
                    <IPStack.Item key={`limitation-${index}`}>
                      <IPText>{limitation}</IPText>
                    </IPStack.Item>
                  ))}
                </IPStack>
              </IPStack.Item>
            )}
            {reports.length > 0 && (
              <IPStack.Item>
                <IPLabel>{t('audit:Execute.Task.AiAudit.Report')}</IPLabel>
                <IPStack tokens={globalStackTokensGapSmall}>
                  {reports.map((report) => (
                    <IPStack.Item key={report.label}>
                      <IPText block styles={globalTextStylesBold}>
                        {report.label}
                      </IPText>
                      <CopilotMarkdown content={report.markdown} withTableStyles skipHtml />
                    </IPStack.Item>
                  ))}
                </IPStack>
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
      </IPStack>
    );
  };

  const renderAiAudit = (result: InternalAuditControlAuditResult) => {
    const item = execution?.items.find((i) => i.entityId === result.controlId);
    const itemName = item ? `${item.code ?? ''} ${item.name ?? ''}`.trim() : `${result.controlId}`;
    const multipleItems = (execution?.items.length ?? 0) > 1;

    return (
      <IPStack.Item key={`ai-${result.controlId}`}>
        <Accordion
          title={
            multipleItems
              ? t('audit:Execute.Task.AiAudit.TitleForItem', { item: itemName })
              : t('audit:Execute.Task.AiAudit.Title')
          }
          showBorder
          maxHeight="none"
          active={
            expandedAiItems.includes(result.controlId) || (!hasToggledResultAccordion && resultAccordionCount === 1)
          }
          onToggle={(expanded) => {
            setHasToggledResultAccordion(true);
            setExpandedAiItems((current) =>
              expanded
                ? [...current.filter((controlId) => controlId !== result.controlId), result.controlId]
                : current.filter((controlId) => controlId !== result.controlId),
            );
          }}
        >
          {renderAiAuditContent(result)}
        </Accordion>
      </IPStack.Item>
    );
  };

  //
  // Main render
  //
  if (props.compact) {
    return (
      <IPStack
        verticalFill
        tokens={globalStackTokensGapSmall}
        styles={{ root: { paddingBottom: 20, paddingRight: 20 } }}
      >
        {isLoading && (
          <IPStack.Item grow>
            <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
              <IPSpinner size={IPSpinnerSize.large} />
            </IPStack>
          </IPStack.Item>
        )}
        {!isLoading && execution && (
          <Fragment>
            <IPStack.Item>
              <IPStack tokens={globalStackTokensGapSmall}>
                {/* the instruction is reference text, not working text: behind the (i) it leaves the
                    height to the checklist the auditee actually works. The title gives way first -
                    the icon never shrinks out of reach */}
                <IPStack
                  horizontal
                  verticalAlign="center"
                  tokens={globalStackTokensGapSmall}
                  styles={{ root: { minWidth: 0, overflow: 'hidden' } }}
                >
                  <IPStack.Item grow styles={{ root: { minWidth: 0, overflow: 'hidden' } }}>
                    <IPText block nowrap variant="large">
                      {renderAuditName()}
                    </IPText>
                  </IPStack.Item>
                  <IPStack.Item styles={{ root: { flexShrink: 0 } }}>
                    <Info message={renderInstruction()} />
                  </IPStack.Item>
                </IPStack>
                {props.compactActions && (
                  <IPStack.Item styles={{ root: { paddingTop: 10, paddingBottom: 10 } }}>
                    {props.compactActions}
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
            <IPStack.Item
              grow
              styles={{
                root: {
                  minHeight: 0,
                  position: 'relative',
                  overflowY: 'auto',
                },
              }}
            >
              {props.compactTaskContent}
            </IPStack.Item>
            <IPStack.Item>
              <IPStack tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPSeparator styles={{ root: { height: 1 } }} />
                </IPStack.Item>
                <IPStack.Item>
                  <IPLabel>{t('audit:Execute.Task.Outcome')}</IPLabel>
                  <IPDropdown
                    options={getOutcomeOptions()}
                    selectedKey={outcome}
                    disabled={resultsFrozen}
                    onChange={(_ev, option) => setOutcome((option?.key as AuditOutcome) ?? AuditOutcome.NotAssessed)}
                  />
                </IPStack.Item>
                <IPStack.Item>
                  <IPLabel>{t('audit:Execute.Task.Conclusion')}</IPLabel>
                  <RichTextEditor
                    html={conclusion}
                    onChange={(value) => setConclusion(value)}
                    onBlur={() => setConclusionUnFocusKey(getRandomId())}
                    unFocusKey={conclusionUnFocusKey}
                    placeholder={t('audit:Execute.Task.ConclusionPlaceholder')}
                    readOnly={resultsFrozen}
                    height={50}
                    maxHeight={300}
                    maxLength={auditConclusionMaxLength}
                    disallowFullscreen
                  />
                </IPStack.Item>
                {resultsFrozen && <IPText variant="small">{getFrozenText()}</IPText>}
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
                styles={{ root: { paddingTop: 10 } }}
              >
                <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                  {props.leadingCommands}
                  <IPIconButton
                    id={`audit-previous-${props.task.taskId}`}
                    iconProps={{ iconName: 'History' }}
                    title={t('audit:Execute.Task.Previous.Title')}
                    disabled={!execution.items.some((item) => item.previous)}
                    onClick={() => setShowPreviousResults(true)}
                  />
                  {aiEnabled && (
                    <IPIconButton
                      id={`audit-ai-${props.task.taskId}`}
                      iconProps={copilotIconProps}
                      title={t('audit:Execute.Task.AiAudit.Title')}
                      disabled={aiResults.length === 0 && !canSuggestConclusion}
                      onClick={() => setShowAiResults(true)}
                    />
                  )}
                </IPStack>
                <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                  {props.trailingCommands}
                </IPStack>
              </IPStack>
              {showPreviousResults && (
                <IPCallout
                  target={`#audit-previous-${props.task.taskId}`}
                  onDismiss={() => setShowPreviousResults(false)}
                  styles={{ root: { width: 440 }, calloutMain: { padding: 10, maxHeight: '70vh', overflowY: 'auto' } }}
                >
                  <IPStack tokens={globalStackTokensGapSmall}>
                    {execution.items.filter((item) => item.previous).map(renderPreviousContent)}
                  </IPStack>
                </IPCallout>
              )}
              {aiEnabled && showAiResults && (
                <IPCallout
                  target={`#audit-ai-${props.task.taskId}`}
                  onDismiss={() => setShowAiResults(false)}
                  styles={{ root: { width: 520 }, calloutMain: { padding: 10, maxHeight: '70vh', overflowY: 'auto' } }}
                >
                  <IPStack tokens={globalStackTokensGapSmall}>
                    {aiResults.map(renderAiAuditContent)}
                    {aiResults.length === 0 && (
                      <IPStack.Item styles={{ root: { padding: 10 } }}>
                        <IPText variant="small">{t('audit:Execute.Task.AiAudit.NoResults')}</IPText>
                      </IPStack.Item>
                    )}
                    {canSuggestConclusion && (
                      <Fragment>
                        {aiResults.length > 0 && (
                          <IPStack.Item>
                            <IPSeparator styles={{ root: { height: 1 } }} />
                          </IPStack.Item>
                        )}
                        {renderSuggestConclusion()}
                      </Fragment>
                    )}
                  </IPStack>
                </IPCallout>
              )}
            </IPStack.Item>
          </Fragment>
        )}
      </IPStack>
    );
  }

  return (
    <Fragment>
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { paddingRight: 10 } }}>
              {getTaskTitleItem() && (
                <AuditScopeItemLink
                  entityType={getTaskTitleItem()!.entityType}
                  entityId={getTaskTitleItem()!.entityId}
                  code={getTaskTitleItem()!.code}
                  name={getTaskTitleItem()!.name}
                  auditTaskId={props.task.taskId}
                  internalAuditId={execution?.internalAuditId ?? props.task.internalAuditId}
                  prominent
                />
              )}

              {isLoading && <IPSpinner />}

              {execution && (
                <IPStack.Item>
                  <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                    <IPStack.Item>
                      <IPLabel>{t('audit:Execute.Task.AuditLabel')}</IPLabel>
                    </IPStack.Item>
                    <IPStack.Item>{renderAuditName()}</IPStack.Item>
                  </IPStack>
                </IPStack.Item>
              )}

              {execution && execution.items.length > 1 ? renderItemList(execution.items) : null}

              {/* the day the auditee is expected to do the work; the times come from the working hours */}
              <IPStack.Item>
                <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                  <IPStack.Item>
                    <IPLabel>{t('audit:Execute.Task.PlannedOn')}</IPLabel>
                  </IPStack.Item>
                  <IPStack.Item>
                    <IPText>{toLocaleDateLong(props.task.startDateTime)}</IPText>
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>

              <IPStack.Item>
                <IPLabel>{t('audit:Execute.Task.Instruction')}</IPLabel>
                <RichTextReadOnly
                  html={props.task.description}
                  placeholder={t('audit:Execute.Task.NoInstruction')}
                  maxHeight={200}
                />
              </IPStack.Item>

              {execution && (
                <IPStack.Item>
                  <IPStack tokens={globalStackTokensGapSmall}>
                    <IPStack.Item>
                      <IPLabel>{t('audit:Execute.Task.Result')}</IPLabel>
                      <IPDropdown
                        options={getOutcomeOptions()}
                        selectedKey={outcome}
                        disabled={resultsFrozen}
                        onChange={(_ev, option) =>
                          setOutcome((option?.key as AuditOutcome) ?? AuditOutcome.NotAssessed)
                        }
                      />
                    </IPStack.Item>
                    <IPStack.Item>
                      <RichTextEditor
                        html={conclusion}
                        onChange={(value) => setConclusion(value)}
                        placeholder={t('audit:Execute.Task.ConclusionPlaceholder')}
                        readOnly={resultsFrozen}
                        maxHeight={300}
                        maxLength={auditConclusionMaxLength}
                        hideRemainingChars
                        disallowFullscreen
                      />
                    </IPStack.Item>
                    {renderSuggestConclusion()}
                    {resultsFrozen && <IPText variant="small">{getFrozenText()}</IPText>}
                  </IPStack>
                </IPStack.Item>
              )}

              {aiEnabled && aiResults.map(renderAiAudit)}

              {execution?.items.map(renderPrevious)}
            </IPStack>
          </IPScrollablePane>
        </IPStack.Item>
      </IPStack>
    </Fragment>
  );
};
